/**
 * Help & Support and Grow with ROI Pros: the few calls these two pages make. Plain fetch,
 * same-origin, with the same CSRF cookie the rest of the app sends.
 */
function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)signal_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : '';
}

async function call<T>(method: string, path: string, body?: unknown, raw?: Blob): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method, credentials: 'same-origin',
    headers: { 'x-csrf-token': csrf(), ...(raw ? { 'content-type': raw.type } : body !== undefined ? { 'content-type': 'application/json' } : {}) },
    body: raw ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error((data as { message?: string }).message ?? 'Request failed'), { status: res.status, data });
  return data as T;
}

export interface HelpConfig { routingAvailable: boolean; bookingUrl: string | null; categories: string[]; interests: string[]; maxScreenshots: number }
export interface GrowthPreferences {
  interests: string[]; promotionsOptOut: boolean; discoveryDismissed: boolean;
  firstWorkflowComplete: boolean; showDiscovery: boolean; bookingUrl: string | null; routingAvailable: boolean;
}

export const helpApi = {
  config: () => call<HelpConfig>('GET', '/help/config'),
  startRequest: (b: { category: string; subject: string; description: string; includeDiagnostics: boolean; browser?: { page: string; userAgent: string; viewport: string } }) =>
    call<{ id: string; diagnostics: Record<string, unknown> }>('POST', '/support/requests', b),
  addScreenshot: (id: string, file: Blob) => call<{ attachmentId: string }>('POST', `/support/requests/${id}/screenshots`, undefined, file),
  submit: (id: string) => call<{ status: 'routed' | 'received' | 'route_failed'; routed: boolean }>('POST', `/support/requests/${id}/submit`, {}),
  preferences: () => call<GrowthPreferences>('GET', '/grow/preferences'),
  savePreferences: (p: Partial<Pick<GrowthPreferences, 'interests' | 'promotionsOptOut' | 'discoveryDismissed'>>) => call<GrowthPreferences>('PUT', '/grow/preferences', p),
  requestConsultation: (b: { interests: string[]; contactName: string; contactEmail: string; contactPhone?: string; company?: string; preferredTime?: string; consentContact: true; consentMarketing: boolean }) =>
    call<{ status: 'routed' | 'received' | 'route_failed'; routed: boolean }>('POST', '/grow/consultations', b),
};

export const CATEGORY_LABEL: Record<string, string> = {
  getting_started: 'Getting started', capture: 'Capturing contacts', review: 'Review and approval', crm: 'CRM connection',
  integrations: 'Zapier and webhooks', browser_companion: 'Browser Companion', billing: 'Billing and plans', other: 'Something else',
};

/** The eight ways ROI Pros helps a business grow, in the customer's words. */
export const INTEREST: Record<string, { title: string; blurb: string }> = {
  more_leads: { title: 'Getting more leads', blurb: 'Bring in more of the right people, consistently.' },
  funnels_landing_pages: { title: 'Funnels and landing pages', blurb: 'Pages that turn visitors into conversations.' },
  email_sms_followup: { title: 'Email and SMS follow-up', blurb: 'Sequences that follow up so you do not have to remember.' },
  crm_sales_process: { title: 'CRM and sales process', blurb: 'A pipeline your team actually keeps up to date.' },
  automation_ai: { title: 'Automation and AI', blurb: 'Hand the repetitive work to systems that do it every time.' },
  team_productivity: { title: 'Team productivity', blurb: 'Fewer handoffs, clearer ownership, less busywork.' },
  business_growth: { title: 'Business growth', blurb: 'A plan for the next stage, not just the next task.' },
  time_team_money: { title: 'Time, team and money', blurb: 'Get more from the hours, people and budget you have.' },
};
